import React, { useEffect, useRef, useState } from "react"
import axios from "axios"

const API = process.env.REACT_APP_API_URL || "http://localhost:3001/api"
const MAX_HISTORICO = 16

interface Msg { role: "user" | "assistant"; content: string; erro?: boolean }

const SUGESTOES = [
  "Quais veículos estão em movimento agora?",
  "Algum veículo está sem sinal?",
  "Quanto gastei com combustível este mês?",
  "Tem manutenção vencida?",
  "Teve algum alerta nas últimas 24 horas?",
]

// texto da Manu -> elementos React. NUNCA usa HTML cru: o que vier (inclusive <tags>) aparece como texto.
// Entende **negrito** e transforma links http(s) em links clicaveis; qualquer outro "esquema" (javascript:, data:) fica como texto.
function renderizar(texto: string): React.ReactNode[] {
  const partes = texto.split(/(\*\*[^*\n]+\*\*|https?:\/\/[^\s<>()]+)/g)
  return partes.map((p, i) => {
    if (/^\*\*[^*\n]+\*\*$/.test(p)) return <b key={i}>{p.slice(2, -2)}</b>
    if (/^https?:\/\//.test(p)) {
      const m = p.match(/^(.*?)([.,;:!?]*)$/)
      const url = m ? m[1] : p
      const resto = m ? m[2] : ""
      const rotulo = /google\.com\/maps/.test(url) ? "📍 Ver no mapa" : url
      return (
        <React.Fragment key={i}>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#38bdf8", textDecoration: "underline" }}>{rotulo}</a>
          {resto}
        </React.Fragment>
      )
    }
    return <React.Fragment key={i}>{p}</React.Fragment>
  })
}

function mensagemDeErro(e: any): string {
  const doServidor = e?.response?.data?.erro
  if (doServidor) return String(doServidor)
  return "Não consegui falar com a Manu agora. Verifique sua conexão e tente de novo."
}

export default function Manu() {
  const [msgs, setMsgs] = useState<Msg[]>([])
  const [texto, setTexto] = useState("")
  const [enviando, setEnviando] = useState(false)
  const areaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = areaRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [msgs, enviando])

  const enviar = async (conteudo: string) => {
    const t = conteudo.trim()
    if (!t || enviando) return
    const nova: Msg = { role: "user", content: t }
    // o historico mandado pro servidor nao inclui as mensagens de erro (nao sao falas da Manu)
    const historico = [...msgs.filter((m) => !m.erro), nova].slice(-MAX_HISTORICO).map((m) => ({ role: m.role, content: m.content }))
    setMsgs((l) => [...l, nova])
    setTexto("")
    setEnviando(true)
    try {
      const r = await axios.post(`${API}/ia/manu/conversar`, { mensagens: historico })
      setMsgs((l) => [...l, { role: "assistant", content: String(r.data?.resposta || "") }])
    } catch (e: any) {
      setMsgs((l) => [...l, { role: "assistant", content: mensagemDeErro(e), erro: true }])
    } finally { setEnviando(false) }
  }

  const aoTeclar = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(texto) }
  }

  const avatar = (
    <div style={{ width: 32, height: 32, borderRadius: 16, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg,#0ea5e9,#8b5cf6)", color: "#fff", fontWeight: 700, fontSize: 14 }}>M</div>
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "20px 24px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700 }}>💬 Manu — assistente de IA</h2>
          <p style={{ fontSize: 13, color: "#64748b" }}>Pergunte sobre a sua frota: posição dos veículos, abastecimentos, manutenções e alertas</p>
        </div>
        {msgs.length > 0 && (
          <button className="btn btn-ghost" style={{ fontSize: 12 }} disabled={enviando} onClick={() => setMsgs([])}>🗑 Nova conversa</button>
        )}
      </div>

      <div ref={areaRef} style={{ flex: 1, overflowY: "auto", padding: "4px 2px 12px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          {avatar}
          <div className="card" style={{ padding: "10px 14px", maxWidth: 560, fontSize: 13 }}>
            Oi! Eu sou a <b>Manu</b>, assistente da MoobiFleet. Posso consultar a <b>posição</b> dos veículos, os <b>abastecimentos</b>,
            as <b>manutenções</b> e os <b>alertas</b> da sua frota. O que você quer saber?
          </div>
        </div>

        {msgs.length === 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginLeft: 42 }}>
            {SUGESTOES.map((s) => (
              <button key={s} className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 12px", borderRadius: 16 }} onClick={() => enviar(s)}>{s}</button>
            ))}
          </div>
        )}

        {msgs.map((m, i) => m.role === "user" ? (
          <div key={i} style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ background: "#0ea5e9", color: "#fff", padding: "9px 14px", borderRadius: "14px 14px 2px 14px", maxWidth: 560, fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {m.content}
            </div>
          </div>
        ) : (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            {avatar}
            <div className="card" data-erro={m.erro ? "1" : undefined}
              style={{ padding: "10px 14px", maxWidth: 620, fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5,
                border: m.erro ? "1px solid #7f1d1d" : undefined, color: m.erro ? "#fca5a5" : undefined }}>
              {m.erro ? m.content : renderizar(m.content)}
            </div>
          </div>
        ))}

        {enviando && (
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {avatar}
            <div className="card" style={{ padding: "10px 14px", fontSize: 12, color: "#94a3b8" }}>Manu está consultando a frota…</div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8 }}>
        <textarea value={texto} onChange={(e) => setTexto(e.target.value)} onKeyDown={aoTeclar} rows={2} maxLength={2000}
          placeholder="Pergunte algo sobre a sua frota… (Enter envia, Shift+Enter quebra a linha)" aria-label="Mensagem para a Manu"
          style={{ flex: 1, resize: "none", padding: "10px 12px", borderRadius: 10, border: "1px solid #2d4166", background: "#1e2f4a", color: "#f1f5f9", fontSize: 13, fontFamily: "inherit" }} />
        <button className="btn btn-primary" style={{ height: 44 }} disabled={enviando || !texto.trim()} onClick={() => enviar(texto)}>
          {enviando ? "..." : "Enviar"}
        </button>
      </div>
      <div style={{ fontSize: 10, color: "#475569", marginTop: 6 }}>
        A Manu só consulta os dados da sua frota e não executa comandos. Ela pode errar: confira no painel as informações importantes.
      </div>
    </div>
  )
}
